/**
 * Choosing how many cards to take from the thrown pile. The top card is free; each one
 * beneath it costs a penalty card from the stock. The thrown cards were all face up, so
 * the player sees them while choosing; the penalty cards stay unseen until they confirm,
 * and nothing reaches the hand until then. Takes the place of the piles while open, so
 * it never covers the hand. Ben's call, 1 October 2026. scope.md §3.4.
 */

import { useState } from 'react';
import type { Card } from '@garbage/shared';
import { plural } from '@garbage/shared';
import { CardBack, CardFace } from './Card.js';
import type { PackId } from '../packs.js';
import { useT } from '../i18n.js';

/** What a take of `count` will cost, as the player is told before they confirm. */
export function digCost(count: number, penaltyApplies: boolean): { penalties: number; grows: number } {
  const penalties = penaltyApplies ? count - 1 : 0;
  return { penalties, grows: count + penalties };
}

export function DiscardPicker({
  reach, penaltyApplies, pack, mustTake, onTake, onCancel,
}: {
  /** The cards they could take, top first: as deep as pile, stock and asks left allow. */
  reach: Card[];
  penaltyApplies: boolean;
  pack: PackId;
  /** They asked for the pile during the window, so backing out to the stock is not on. */
  mustTake: boolean;
  onTake: (count: number) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const max = reach.length;
  const [count, setCount] = useState(1);
  const { penalties, grows } = digCost(count, penaltyApplies);

  const costLine = count === 1
    ? t('dig.free')
    : penaltyApplies
      ? plural(t, 'dig.cost', penalties, { count, penalties, grows })
      : t('dig.costFree', { count });

  return (
    <section className="flex flex-col items-center gap-2 px-3 pb-1" data-testid="discard-picker" aria-live="polite">
      <p className="text-center text-sm font-bold text-sun">{t('dig.title')}</p>
      <p className="text-center text-xs text-bone/70">{t('dig.hint')}</p>

      {/* The thrown cards, top first. Tapping one takes everything down to it. */}
      <div className="flex max-w-full flex-wrap justify-center gap-1" data-testid="dig-reach">
        {reach.map((card, i) => (
          <button
            key={card.id}
            type="button"
            data-testid={`dig-card-${i + 1}`}
            aria-pressed={i < count}
            onClick={() => setCount(i + 1)}
            className={i < count ? 'rounded-md ring-2 ring-sun' : 'opacity-40'}
          >
            <CardFace card={card} size="small" pack={pack} />
          </button>
        ))}
      </div>
      {/* The penalty cards, face down: what they will get, unseen until they choose. */}
      {penaltyApplies && count > 1 && (
        <div className="flex items-center gap-1" data-testid="dig-penalties">
          <span className="text-xs text-bone/60">+</span>
          {Array.from({ length: count - 1 }, (_, i) => (
            <div key={i} className={i > 0 ? '-ml-6' : ''}><CardBack size="small" pack={pack} /></div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="dig-fewer"
          aria-label={t('dig.fewer')}
          disabled={count <= 1}
          onClick={() => setCount((c) => Math.max(1, c - 1))}
          className="h-11 w-11 rounded-full bg-white/15 text-2xl font-black disabled:opacity-30"
        >
          −
        </button>
        <span className="min-w-8 text-center text-2xl font-black" data-testid="dig-count">{count}</span>
        <button
          type="button"
          data-testid="dig-more"
          aria-label={t('dig.more')}
          disabled={count >= max}
          onClick={() => setCount((c) => Math.min(max, c + 1))}
          className="h-11 w-11 rounded-full bg-white/15 text-2xl font-black disabled:opacity-30"
        >
          +
        </button>
      </div>

      {/* Say what it costs before they tap, not after. §8.2 */}
      <p className="text-center text-sm font-bold" data-testid="dig-cost">{costLine}</p>
      {count >= max && max > 1 && <p className="text-xs text-bone/60">{t('dig.limit')}</p>}

      <div className="flex gap-2">
        {!mustTake && (
          <button
            type="button"
            data-testid="dig-cancel"
            onClick={onCancel}
            className="rounded-2xl bg-white/15 px-4 py-3 font-bold active:scale-95"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="button"
          data-testid="dig-take"
          onClick={() => onTake(count)}
          className="rounded-2xl bg-sun px-5 py-3 font-black text-ink active:scale-95"
        >
          {plural(t, 'dig.take', count, { count })}
        </button>
      </div>
    </section>
  );
}
